import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, X, Building, CheckCircle2, AlertCircle, Upload } from 'lucide-react';
import { apiClient } from '../../services/apiClient';

interface EnterpriseVerificationCardProps {
    status: 'unverified' | 'pending' | 'verified';
    isPersonalVerified: boolean;
    onVerifySuccess: () => void;
}

const MAX_FILE_MB = 5;

function compressImage(file: File, maxWidth = 1600, quality = 0.8): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let w = img.width, h = img.height;
            if (w > maxWidth) { h = (h * maxWidth) / w; w = maxWidth; }
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}

const PhotoUpload: React.FC<{ label: string; sublabel: string; value: string; onChange: (v: string) => void }> = ({ label, sublabel, value, onChange }) => {
    const ref = useRef<HTMLInputElement>(null);
    const [error, setError] = useState('');
    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError('');
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            setError(`文件不能超过 ${MAX_FILE_MB}MB`);
            return;
        }
        try {
            const compressed = await compressImage(file);
            onChange(compressed);
        } catch { setError('图片处理失败'); }
    };
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {value ? (
                <div className="relative">
                    <img src={value} alt={label} className="w-full h-32 object-contain rounded-lg border border-gray-200 bg-gray-50" />
                    <button onClick={() => onChange('')} className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full hover:bg-black/70"><X className="w-3 h-3" /></button>
                </div>
            ) : (
                <div onClick={() => ref.current?.click()} className="py-6 border-2 border-dashed border-gray-200 rounded-lg hover:border-gray-300 transition-colors flex flex-col items-center gap-1 text-gray-500 cursor-pointer">
                    <Upload className="w-5 h-5" />
                    <span className="text-xs font-medium">{sublabel}</span>
                    <span className="text-xs text-gray-400">JPG/PNG，不超过 {MAX_FILE_MB}MB</span>
                </div>
            )}
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            <input ref={ref} type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </div>
    );
};

const EnterpriseVerifyModal = ({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) => {
    const [step, setStep] = useState<'form' | 'success' | 'error'>('form');
    const [companyName, setCompanyName] = useState('');
    const [creditCode, setCreditCode] = useState('');
    const [legalRepName, setLegalRepName] = useState('');
    const [licensePhoto, setLicensePhoto] = useState('');
    const [idFrontPhoto, setIdFrontPhoto] = useState('');
    const [idBackPhoto, setIdBackPhoto] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => { if (!isOpen) { setStep('form'); setLoading(false); setErrorMsg(''); } }, [isOpen]);

    const allFilled = companyName.trim() && creditCode.trim() && legalRepName.trim() && licensePhoto && idFrontPhoto && idBackPhoto;

    const handleSubmit = async () => {
        if (!allFilled) return;
        setLoading(true); setErrorMsg('');
        try {
            const res = await apiClient.post<any>('/api/v1/verification/enterprise/submit', {
                companyName: companyName.trim(), creditCode: creditCode.trim(), legalRepName: legalRepName.trim(),
                licensePhoto, idFrontPhoto, idBackPhoto,
            });
            if (res.success) { setStep('success'); setTimeout(() => onSuccess(), 1500); }
            else { setErrorMsg((res as any).error || '提交失败'); setStep('error'); }
        } catch (e: any) { setErrorMsg(e.message || '提交失败'); setStep('error'); }
        setLoading(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 text-left">
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h2 className="text-xl font-bold flex items-center gap-2"><Building className="w-5 h-5" /> 企业实名认证</h2>
                    <button onClick={onClose} disabled={step === 'success'} className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6">
                    {step === 'form' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">请填写企业信息并上传相关证件照片。</p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">企业名称</label>
                                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="营业执照上的企业全称" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">统一社会信用代码</label>
                                <input value={creditCode} onChange={e => setCreditCode(e.target.value)} placeholder="18位统一社会信用代码" maxLength={18} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">法定代表人姓名</label>
                                <input value={legalRepName} onChange={e => setLegalRepName(e.target.value)} placeholder="法定代表人姓名" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100" />
                            </div>
                            <PhotoUpload label="营业执照" sublabel="上传营业执照照片" value={licensePhoto} onChange={setLicensePhoto} />
                            <div className="grid grid-cols-2 gap-3">
                                <PhotoUpload label="身份证正面" sublabel="人像面" value={idFrontPhoto} onChange={setIdFrontPhoto} />
                                <PhotoUpload label="身份证反面" sublabel="国徽面" value={idBackPhoto} onChange={setIdBackPhoto} />
                            </div>
                            <button onClick={handleSubmit} disabled={loading || !allFilled} className="w-full py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors">
                                {loading ? '正在提交...' : '提交审核'}
                            </button>
                            <p className="text-xs text-gray-400 text-center">提交后由平台人工审核，通常 1-3 个工作日内完成。</p>
                        </div>
                    )}
                    {step === 'success' && (
                        <div className="py-12 flex flex-col items-center">
                            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4"><CheckCircle2 className="w-10 h-10 text-green-500" /></div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">提交成功</h3>
                            <p className="text-gray-500 text-sm">您的企业认证申请已提交，请等待审核。</p>
                        </div>
                    )}
                    {step === 'error' && (
                        <div className="py-8 flex flex-col items-center space-y-4">
                            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center"><AlertCircle className="w-10 h-10 text-red-500" /></div>
                            <p className="text-sm text-red-600 text-center">{errorMsg}</p>
                            <button onClick={() => { setStep('form'); setErrorMsg(''); }} className="px-6 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800">重试</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const EnterpriseVerificationCard: React.FC<EnterpriseVerificationCardProps> = ({ status, onVerifySuccess }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    return (
        <div className="border border-gray-100 rounded-2xl p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
                <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6 text-yellow-500" /></div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-900 flex items-center">
                            企业认证
                            {status === 'verified' && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium ml-2">已认证</span>}
                            {status === 'pending' && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium ml-2">审核中</span>}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-md">
                            获得黄色企业标识 <ShieldCheck className="w-3 h-3 text-yellow-500 inline align-middle" />，解锁对公账户收款、发票开具功能并支持电子合同签署。
                        </p>
                    </div>
                </div>
                <div>
                    {status === 'verified' ? <button className="px-5 py-2 bg-gray-100 text-gray-400 rounded-lg text-sm font-medium cursor-default">已完成</button>
                    : status === 'pending' ? <button className="px-5 py-2 bg-orange-50 text-orange-600 rounded-lg text-sm font-medium cursor-default">审核中</button>
                    : <button onClick={() => setIsModalOpen(true)} className="px-5 py-2 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">去认证</button>}
                </div>
            </div>
            <EnterpriseVerifyModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSuccess={() => { setIsModalOpen(false); onVerifySuccess(); }} />
        </div>
    );
};

export default EnterpriseVerificationCard;
